import { useState, useEffect } from 'react';
import { Topbar, Sidebar, Loader, Debtor, Pagination, Search } from "../../../components";
import { authStaff } from "../../../utils/constants";
import { adminPortalRequest } from "../../../Requests/getRequests";
import { useDispatch, useSelector } from "react-redux";
import { selectAccessToken } from "../../../slices/staffSlice";
import { show } from '../../../slices/toastSlice';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserIcon, CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';

function Index() {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [clientList, setClientList] = useState();
    const [defaultData, setDefaultData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [metadata, setMetadata] = useState(null);
    const [currentPage, setCurrentPage] = useState(metadata?.currentPage || 1);
    const nPages = metadata?.totalPages;

    const handlePagination = async(page) => {
        setIsLoading(true);
        const res = await adminPortalRequest(`/auth/staff/client/list`, "GET", authStaff(accessToken), { page: page || 1 }, {});
        setIsLoading(false);

        if(res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }
        if(res?.err) { dispatch(show({ type: "error", state: true, message: res?.err })); return; }

        setClientList(res.clients);
        setDefaultData(res.clients);
        setMetadata(res.metadata);
    };

    useEffect(() => {
        let unsubscribed = false;
        const load = async() => {
            setIsLoading(true);
            const res = await adminPortalRequest(`/auth/staff/client/list`, "GET", authStaff(accessToken), { page: 1 }, {});
            setIsLoading(false);
            if(unsubscribed) return;
            if(res?.code === 401) {
                sessionStorage.clear();
                navigate("/login");
                dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
                return;
            }
            if(res?.err) { dispatch(show({ type: "error", state: true, message: res?.err })); return; }
            setClientList(res.clients);
            setDefaultData(res.clients);
            setMetadata(res.metadata);
        };
        load();
        return () => { unsubscribed = true; };
    }, [accessToken, dispatch, navigate]);

    const verifiedCount = clientList?.filter(c => c.isVerified).length ?? 0;

    return (
        <div className="main-container">
            <Topbar />
            <Sidebar />

            <main className="main-content">
                <section className="section-container space-y-6">

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0">
                                <HugeiconsIcon icon={UserIcon} size={18} className="text-purple-600" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Clients</h1>
                                    {metadata?.totalCount > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded-full">
                                            {metadata.totalCount}
                                        </span>
                                    )}
                                    {clientList && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full">
                                            <HugeiconsIcon icon={CheckmarkCircle01Icon} size={11} />
                                            {verifiedCount} verified
                                        </span>
                                    )}
                                    {isLoading && <Loader color="text-gray-400" size="w-4 h-4" />}
                                </div>
                                <p className="text-sm text-gray-400">View and manage registered clients</p>
                            </div>
                        </div>

                        <Search
                            data={clientList}
                            setData={setClientList}
                            placeholder="Search clients"
                            defaultData={defaultData}
                        />
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50/60 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-700">
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-[22%]">Name</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide w-[28%]">Email Address</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone Number</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Created On</th>
                                    <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                {clientList && clientList.length > 0 ? (
                                    clientList.map((item, index) => (
                                        <Debtor key={index} item={item} setClientList={setClientList} />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-5 py-10 text-center text-sm text-gray-400">
                                            {isLoading ? 'Loading...' : 'No clients found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {clientList && (
                            <Pagination
                                nPages={nPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                length={metadata.totalCount}
                                handlePagination={handlePagination}
                            />
                        )}
                    </div>

                </section>
            </main>
        </div>
    );
}

export default Index;
